/**
 * usePDFExport - Hook for exporting reports to PDF
 * Handles chart rendering, PDF generation, and download
 */

import { useState, useCallback } from 'react';
import { renderChartsToSVG } from '@/components/pdf';
import { toast } from '@/hooks/useToast';
import type { Report } from '@/lib/types';

export interface UsePDFExportReturn {
  exportToPDF: (report: Report) => Promise<void>;
  isExporting: boolean;
  progress: number;
  stage: string;
}

export function usePDFExport(): UsePDFExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('');

  const exportToPDF = useCallback(async (report: Report) => {
    setIsExporting(true);
    setProgress(0);
    setStage('Preparing export...');

    try {
      // Lazy-load the PDF stack (jsPDF + svg2pdf + fonts) so it stays out of the main bundle
      const { generateReportPDF, generatePDFFilename, extractChartConfigs } = await import('@/lib/pdf');

      // Step 1: Extract chart configurations from report content
      setStage('Extracting charts...');
      setProgress(5);
      const chartConfigs = extractChartConfigs(report.content);

      // Step 2: Render charts to SVG
      setStage('Rendering charts...');
      const chartSVGs = await renderChartsToSVG(chartConfigs, (completed, total) => {
        const chartProgress = 10 + (completed / total) * 40;
        setProgress(chartProgress);
      });

      // Step 3: Generate PDF
      setStage('Generating PDF...');
      setProgress(50);
      const pdfBlob = await generateReportPDF({
        report,
        chartSVGs,
        onProgress: (stageText, progressValue) => {
          setStage(stageText);
          // Map generator progress (10-100) to our range (50-95)
          setProgress(50 + (progressValue / 100) * 45);
        },
      });

      // Step 4: Trigger download
      setStage('Downloading...');
      setProgress(95);

      const filename = generatePDFFilename(report);
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setProgress(100);
      setStage('Complete');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast({
        title: 'PDF export failed',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      // Reset state after a short delay
      setTimeout(() => {
        setIsExporting(false);
        setProgress(0);
        setStage('');
      }, 500);
    }
  }, []);

  return {
    exportToPDF,
    isExporting,
    progress,
    stage,
  };
}
